import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { packetLossStatuses } from 'APP/Lib/VideoSession/constants'
import type {
  VideoSessionState,
  ConnectToSessionPayload,
  PublisherStatsPayload,
  PacketLossEventPayload,
  StreamPayload,
  OnDemandVideoCallRequestedPayload,
  PatientEndedCallPayload,
  PatientAcceptedCallPayload,
  SaveLastVideoCallInfoPayload,
  OnDemandVideoCallEndedPayload,
  PatientDeclinedCallPayload,
  OnDemandVideoCallHandledByRemotePayload,
  TrackVideoQualityStatsPayload,
} from './types'
import { logger } from 'APP/Lib/Datadog'

const INITIAL_STATE: VideoSessionState = {
  sessionId: null,
  episodeId: null,
  connected: false,
  connecting: false,
  connections: [],
  streams: [],
  practitionerId: null,
  lastCallAcceptedAt: null,
  patientIsMuted: false,
  practitionerIsMuted: false,
  keepAliveIntervalId: null,
  publisherStats: {
    numPacketLossEvents: 0,
    highestLossPercent: 0,
    totalPacketsSent: 0,
    totalPacketsLost: 0,
    currentLossPercent: 0,
    currentLossStatus: packetLossStatuses.OKAY as keyof typeof packetLossStatuses,
  },
  lastVideoCall: {
    practitionerId: null,
    episodeId: null,
  },
  declined: false,
}

const resetCallState = (state: VideoSessionState) => {
  state.episodeId = null
  state.practitionerId = null
  state.patientIsMuted = false
  state.practitionerIsMuted = false
}

const resetConnectionState = (state: VideoSessionState) => {
  state.connected = false
  state.connecting = false
  resetCallState(state)
}

const { actions, reducer } = createSlice({
  name: 'videoSession',
  initialState: INITIAL_STATE,
  reducers: {
    connectToSession: (state, action: PayloadAction<ConnectToSessionPayload>) => {
      return {
        ...INITIAL_STATE,
        sessionId: action.payload.sessionId,
        connecting: true,
        lastCallAcceptedAt: state.lastCallAcceptedAt,
      }
    },
    setPublisherStats: (state, action: PayloadAction<PublisherStatsPayload>) => {
      const { totalPacketsSent, totalPacketsLost, currentLossPercent } = action.payload
      state.publisherStats.totalPacketsSent = totalPacketsSent
      state.publisherStats.totalPacketsLost = totalPacketsLost
      state.publisherStats.currentLossPercent = currentLossPercent
      state.publisherStats.highestLossPercent = Math.max(
        currentLossPercent,
        state.publisherStats.highestLossPercent
      )
    },
    resetPublisherStats: (state) => {
      state.publisherStats = {
        ...state.publisherStats,
        totalPacketsSent: 0,
        totalPacketsLost: 0,
        currentLossPercent: 0,
        currentLossStatus: packetLossStatuses.OKAY as keyof typeof packetLossStatuses,
      }
    },
    publisherPacketLossEvent: (state, action: PayloadAction<PacketLossEventPayload>) => {
      state.publisherStats.numPacketLossEvents += 1
      state.publisherStats.currentLossStatus = action.payload.currentLossStatus
    },
    sessionConnected: (state) => {
      state.connecting = false
      state.connected = true
    },
    connectionCreated: (state, action: PayloadAction<{ connection: { connectionId: string } }>) => {
      const connectionId = action.payload.connection.connectionId
      if (connectionId) {
        state.connections.push(connectionId)
      }
    },
    connectionDestroyed: (
      state,
      action: PayloadAction<{ connection: { connectionId: string } }>
    ) => {
      const connectionId = action.payload.connection.connectionId
      if (connectionId) {
        state.connections = state.connections.filter((id) => id !== connectionId)
      }
    },
    setKeepAliveIntervalId: (
      state,
      action: PayloadAction<{ intervalId: ReturnType<typeof setInterval> | null }>
    ) => {
      state.keepAliveIntervalId = action.payload.intervalId
    },
    platformMutedAudio: (state) => {
      state.practitionerIsMuted = true
    },
    platformUnmutedAudio: (state) => {
      state.practitionerIsMuted = false
    },
    platformStreamCreated: (state, action: PayloadAction<StreamPayload>) => {
      state.streams.push(action.payload.stream)
    },
    platformStreamDestroyed: (state, action: PayloadAction<StreamPayload>) => {
      state.streams = state.streams.filter(
        (stream) => stream.streamId !== action.payload.stream.streamId
      )
    },
    patientMutedAudio: (state) => {
      state.patientIsMuted = true
    },
    patientUnmutedAudio: (state) => {
      state.patientIsMuted = false
    },
    handledByRemote: () => {},
    saveLastVideoCallInfo: (state, action: PayloadAction<SaveLastVideoCallInfoPayload>) => {
      const { callerId, episodeId } = action.payload
      state.practitionerId = callerId
      state.episodeId = episodeId
      state.lastVideoCall = {
        practitionerId: callerId,
        episodeId: episodeId,
      }
    },

    disconnectFromSession: (state) => {
      return {
        ...INITIAL_STATE,
        lastCallAcceptedAt: state.lastCallAcceptedAt,
      }
    },

    sessionDisconnected: resetConnectionState,
    sessionFailed: resetConnectionState,

    platformEndedCall: resetCallState,
    malformedSignal: resetCallState,
    unhandledSignal: resetCallState,

    patientAskingAudioPermission: () => {},
    patientAskingVideoPermission: () => {},
    patientAcceptedAudioPermission: () => {},
    patientAcceptedVideoPermission: () => {},
    patientDeclinedAudioPermission: () => {},
    patientDeclinedVideoPermission: () => {},
    patientAcceptedCall: (state, action: PayloadAction<PatientAcceptedCallPayload>) => {
      state.lastCallAcceptedAt = action.payload.lastCallAcceptedAt || null
    },
    patientEndedCall: (state, action: PayloadAction<PatientEndedCallPayload>) => {
      const { declined } = action.payload

      logger.info('Dispatched patientEndedCall action', { declined })

      state.declined = declined
    },
    patientDeclinedCall: (_state, action: PayloadAction<PatientDeclinedCallPayload>) => {
      const { onDemandSessionId } = action.payload

      logger.info('Dispatched patientDeclinedCall action', {
        onDemandSessionId,
      })
    },
    onDemandCallRequested: (_state, action: PayloadAction<OnDemandVideoCallRequestedPayload>) => {
      const { callerId, episodeId, sessionId } = action.payload
      // We only log for now
      logger.info('Dispatched onDemandCallRequested action', {
        callerId,
        episodeId,
        onDemandSessionId: sessionId,
      })
    },
    onDemandCallHandledByRemote: (
      _state,
      action: PayloadAction<OnDemandVideoCallHandledByRemotePayload>
    ) => {
      const { onDemandSessionId } = action.payload

      logger.info('Dispatched onDemandCallHandledByRemote action', {
        onDemandSessionId,
      })
    },
    onDemandCallEnded: (_state, action: PayloadAction<OnDemandVideoCallEndedPayload>) => {
      const { onDemandSessionId } = action.payload
      logger.info('Dispatched onDemandCallEnded action', {
        onDemandSessionId,
      })
    },
    trackVideoQualityStats: (_state, _action: PayloadAction<TrackVideoQualityStatsPayload>) => {},
    videoCallDismissed: resetCallState,
  },
})

export { actions as videoSessionActions, INITIAL_STATE }

export const VIDEO_CALL_END_ACTIONS = [
  actions.patientEndedCall,
  actions.platformEndedCall,
  actions.platformStreamDestroyed,
  actions.handledByRemote,
  actions.onDemandCallEnded,
  actions.onDemandCallHandledByRemote,
]

/** Actions that end the video call and must clear the brownfield keepalive interval. */
export const KEEPALIVE_CLEANUP_ACTIONS = [
  actions.patientEndedCall,
  actions.platformEndedCall,
  actions.platformStreamDestroyed,
  actions.handledByRemote,
  // We don't end the keepalive when dispatching onDemandCallHandledByRemote or onDemandCallEnded,
  // because their effects are conditional.
  // If appropriate, these flows will eventually dispatch videoCallDismissed.
  // TODO: clean up this list to the minimum required.
  actions.sessionFailed,
  actions.disconnectFromSession,
  actions.videoCallDismissed,
]

export default reducer
